import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getLiberiaDirections, type LiberiaDirectionsResult } from '@/utils/liberiaApi';
import { getBrowserDirections } from '@/utils/browserDirections';
import DirectionsRouteMap from '@/components/search/DirectionsRouteMap';
import type { GoogleTravelMode, RoutePoint } from '@/utils/googleDirectionsLink';

const MODE_LABELS: Record<GoogleTravelMode, string> = {
  walking: 'Walk',
  driving: 'Drive',
  bicycling: 'Bike',
  transit: 'Transit',
};

export function travelModeLabel(mode: GoogleTravelMode): string {
  return MODE_LABELS[mode];
}

interface ShowDirectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  origin: RoutePoint | null;
  destination: RoutePoint | null;
  mode: GoogleTravelMode;
}

/**
 * Same turn-by-turn popup for an address card and for Get Directions.
 * The Google route is drawn here. Capacitor treats window.open as Safari or Chrome,
 * so this dialog is the navigation screen and it does not leave the app.
 * Steps come from the navigation API, with the browser directions call as backup.
 */
export default function ShowDirectionDialog({
  open,
  onOpenChange,
  origin,
  destination,
  mode,
}: ShowDirectionDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [route, setRoute] = useState<LiberiaDirectionsResult | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!origin || !destination) {
      setLoading(false);
      setRoute(null);
      setError('Location is needed to show directions.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setRoute(null);

    (async () => {
      try {
        let result: LiberiaDirectionsResult;
        try {
          result = await getLiberiaDirections({ origin, destination, mode });
        } catch {
          result = await getBrowserDirections({ origin, destination, mode });
        }
        if (cancelled) return;
        setRoute(result);
        if (!result.steps?.length) {
          setError('No turn-by-turn steps for this route.');
        }
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not load directions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, origin?.lat, origin?.lng, destination?.lat, destination?.lng, mode]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Show Direction</DialogTitle>
          <p className="text-sm text-muted-foreground">{travelModeLabel(mode)}</p>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto pr-1">
          {loading ? (
            <div className="flex items-center gap-3 py-6 text-sm text-gray-600">
              <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-blue-600" />
              Building turn-by-turn directions…
            </div>
          ) : null}
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {route?.overviewPolyline ? (
            <div className="mb-3 h-64 overflow-hidden rounded-lg border border-gray-300">
              {/* Google's overview path, drawn on the map that already lives in the app. */}
              <DirectionsRouteMap
                polyline={route.overviewPolyline}
                origin={origin}
                destination={destination}
                className="h-full w-full"
              />
            </div>
          ) : null}
          {route?.steps?.length ? (
            <div className="space-y-3">
              {route.durationText || route.distanceText ? (
                <p className="text-sm text-gray-700">
                  {[route.durationText, route.distanceText].filter(Boolean).join(' · ')}
                </p>
              ) : null}
              <ol className="space-y-3">
                {route.steps.map((step, index) => (
                  <li key={`${index}-${step.instruction}`} className="flex gap-3 text-sm">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-xs font-semibold text-blue-800">
                      {index + 1}
                    </span>
                    <div>
                      <p className="text-gray-900">{step.instruction}</p>
                      {step.distanceText || step.durationText ? (
                        <p className="text-gray-500">
                          {[step.distanceText, step.durationText].filter(Boolean).join(' · ')}
                        </p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}
